import { useState, useEffect } from "react";
import axios from "axios";
import { Trophy, Medal, Star, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState({ top_by_score: [], most_consistent: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/analytics/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-6 h-6 text-[#FFD700]" />;
    if (index === 1) return <Medal className="w-6 h-6 text-[#C0C0C0]" />;
    if (index === 2) return <Medal className="w-6 h-6 text-[#CD7F32]" />;
    return <span className="w-6 h-6 flex items-center justify-center font-bold text-[#5C5C5C]">{index + 1}</span>;
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const LeaderboardList = ({ data, type }) => (
    <div className="space-y-3">
      {data.length > 0 ? (
        data.map((user, index) => (
          <div 
            key={user.id}
            data-testid={`leaderboard-item-${index}`}
            className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
              index < 3 ? 'bg-primary/5 border border-primary/10' : 'bg-secondary/50'
            }`}
          >
            <div className="flex-shrink-0">
              {getRankIcon(index)}
            </div>
            <Avatar className="w-12 h-12 bg-primary text-white">
              <AvatarFallback className="bg-primary text-white font-semibold">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#1A1A1A] truncate">{user.name}</p>
              <p className="text-sm text-[#5C5C5C]">{user.department}</p>
            </div>
            <div className="text-right">
              {type === 'score' ? (
                <>
                  <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {user.average_score}
                  </p>
                  <p className="text-xs text-[#5C5C5C]">avg score</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-accent" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {user.sessions_completed}
                  </p>
                  <p className="text-xs text-[#5C5C5C]">sessions</p>
                </>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="empty-state py-12">
          <Trophy className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-[#5C5C5C]">No data available yet</p>
          <p className="text-sm text-[#9CA3AF] mt-1">Complete sessions to appear on the leaderboard</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Leaderboard
        </h1>
        <p className="text-[#5C5C5C] mt-1">
          Recognizing top performers and consistency champions
        </p>
      </div>

      {/* Top 3 Podium */}
      {leaderboard.top_by_score?.length >= 3 && (
        <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
          {/* Second Place */}
          <div className="flex flex-col items-center pt-8">
            <Avatar className="w-16 h-16 bg-[#C0C0C0] text-white border-4 border-white shadow-lg">
              <AvatarFallback className="bg-[#C0C0C0] text-white font-bold text-lg">
                {getInitials(leaderboard.top_by_score[1].name)}
              </AvatarFallback>
            </Avatar>
            <div className="mt-3 text-center">
              <p className="font-semibold text-[#1A1A1A] text-sm">{leaderboard.top_by_score[1].name}</p>
              <p className="text-2xl font-bold text-[#C0C0C0]">{leaderboard.top_by_score[1].average_score}</p>
            </div>
            <div className="w-full h-20 bg-[#C0C0C0]/20 rounded-t-xl mt-2 flex items-center justify-center">
              <Medal className="w-8 h-8 text-[#C0C0C0]" />
            </div>
          </div>

          {/* First Place */}
          <div className="flex flex-col items-center">
            <div className="relative">
              <Avatar className="w-20 h-20 bg-[#FFD700] text-white border-4 border-white shadow-lg">
                <AvatarFallback className="bg-[#FFD700] text-white font-bold text-xl">
                  {getInitials(leaderboard.top_by_score[0].name)}
                </AvatarFallback>
              </Avatar>
              <Star className="absolute -top-2 -right-2 w-8 h-8 text-[#FFD700] fill-[#FFD700]" />
            </div>
            <div className="mt-3 text-center">
              <p className="font-semibold text-[#1A1A1A]">{leaderboard.top_by_score[0].name}</p>
              <p className="text-3xl font-bold text-[#FFD700]">{leaderboard.top_by_score[0].average_score}</p>
            </div>
            <div className="w-full h-28 bg-[#FFD700]/20 rounded-t-xl mt-2 flex items-center justify-center">
              <Trophy className="w-10 h-10 text-[#FFD700]" />
            </div>
          </div>

          {/* Third Place */}
          <div className="flex flex-col items-center pt-12">
            <Avatar className="w-14 h-14 bg-[#CD7F32] text-white border-4 border-white shadow-lg">
              <AvatarFallback className="bg-[#CD7F32] text-white font-bold">
                {getInitials(leaderboard.top_by_score[2].name)}
              </AvatarFallback>
            </Avatar>
            <div className="mt-3 text-center">
              <p className="font-semibold text-[#1A1A1A] text-sm">{leaderboard.top_by_score[2].name}</p>
              <p className="text-xl font-bold text-[#CD7F32]">{leaderboard.top_by_score[2].average_score}</p>
            </div>
            <div className="w-full h-16 bg-[#CD7F32]/20 rounded-t-xl mt-2 flex items-center justify-center">
              <Medal className="w-6 h-6 text-[#CD7F32]" />
            </div>
          </div>
        </div>
      )}

      {/* Full Leaderboard */}
      <Card className="stats-card">
        <CardContent className="p-0">
          <Tabs defaultValue="score" className="w-full">
            <TabsList className="w-full rounded-none border-b bg-transparent p-0">
              <TabsTrigger 
                value="score" 
                data-testid="tab-score"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-4"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Top Performers
              </TabsTrigger>
              <TabsTrigger 
                value="consistency" 
                data-testid="tab-consistency"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-4"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Most Consistent
              </TabsTrigger>
            </TabsList>
            <TabsContent value="score" className="p-6">
              <LeaderboardList data={leaderboard.top_by_score || []} type="score" />
            </TabsContent>
            <TabsContent value="consistency" className="p-6">
              <LeaderboardList data={leaderboard.most_consistent || []} type="consistency" />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
